import "./free-shipping-progress.scss";

const SPINNER_VISIBLE_CLASS = "free-shipping-progress__loading--visible";

export class FreeShippingProgressController {
  #containerSelector;

  #$container = null;

  constructor(containerSelector = "") {
    this.#containerSelector = containerSelector;
  }

  // Private container getter with caching
  get #$cachedContainer() {
    this.#$container ??= document.querySelector(this.#containerSelector);
    return this.#$container;
  }

  // Public getters
  get $progressContent() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-content");
  }

  // Private getters
  get #$progressSpinner() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-spinner");
  }

  get #$progressComplete() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-complete");
  }

  get #$progressShortfall() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-shortfall");
  }

  get #$progressBarContainer() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-container");
  }

  get #$progressShortfallAmount() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-shortfall-amount");
  }

  get #$progressBar() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar");
  }

  get #$progressThreshold() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-threshold");
  }

  get #$accountPromo() {
    return this.#$cachedContainer?.querySelector(".js-progress-bar-account-promo");
  }

  get #currencySign() {
    return this.#$progressBarContainer?.dataset?.currencySign || "$";
  }

  // Private methods
  #updateContainerVisibility(threshold) {
    if (threshold > 0) {
      this.#$progressBarContainer?.classList.remove("hidden");
    } else {
      this.#$progressBarContainer?.classList.add("hidden");
    }
  }

  #updateShortfallState({ complete, threshold, total, currencySign }) {
    if (complete < 100) {
      const shortage = (threshold - total).toFixed(2);
      if (this.#$progressShortfallAmount) {
        this.#$progressShortfallAmount.innerHTML = `${currencySign}${shortage}`;
      }
      this.#$progressComplete?.classList.add("hidden");
      this.#$progressShortfall?.classList.remove("hidden");
    } else {
      this.#$progressShortfall?.classList.add("hidden");
      this.#$progressComplete?.classList.remove("hidden");
    }
  }

  #updateProgressBarDisplay({ complete, threshold, currencySign }) {
    if (this.#$progressBar) {
      this.#$progressBar.style.width = `${complete}%`;
      this.#$progressBar.classList.toggle("progress-bar__line--complete", complete >= 100);
    }

    if (this.#$progressThreshold) {
      this.#$progressThreshold.innerHTML = `${currencySign}${threshold}`;
    }
  }

  #updateAccountPromo(showFreeShippingWithAccount) {
    if (this.#$accountPromo) {
      this.#$accountPromo.innerHTML = showFreeShippingWithAccount
        ? "Available <br> with a FREE Account!"
        : "";
    }
  }

  // Public methods
  showSpinner() {
    if (this.$progressContent && this.#$progressSpinner) {
      this.$progressContent.classList.add("hidden");
      this.#$progressSpinner.classList.add(SPINNER_VISIBLE_CLASS);
    }
  }

  hideSpinner() {
    this.#$progressSpinner?.classList.remove(SPINNER_VISIBLE_CLASS);
  }

  updateProgressBar({ total, threshold, showFreeShippingWithAccount = false }) {
    if (!this.$progressContent) return;

    const currencySign = this.#currencySign;
    const parsedTotal = typeof total === "string" ? Number.parseFloat(total) : total;
    const complete = (parsedTotal / threshold) * 100;
    const values = { complete, threshold, total: parsedTotal, currencySign };

    this.#updateContainerVisibility(threshold);
    this.#updateShortfallState(values);
    this.#updateProgressBarDisplay(values);

    this.$progressContent.classList.remove("hidden");
    this.#updateAccountPromo(showFreeShippingWithAccount);
  }
}

export default FreeShippingProgressController;
